import { motion, AnimatePresence } from "motion/react"
import { cn } from "@/utils/Functions"
import { useState } from "react"
import type { ScreenProps } from "./types"
import { ContractTypeIcon } from "@/assets/icons/contracts"

export const ContractTypeSelection = ({ selectedScreen, setSelectedScreen }: ScreenProps) => {
  const contractsByTab = {
    'Music': [
      { label: 'Recording Agreement', value: 'recording-agreement' },
      { label: 'Publishing Agreement', value: 'publishing-agreement' },
      { label: 'Producer Agreement', value: 'producer-agreement' },
      { label: 'Live Performance Agreement', value: 'live-performance' }
    ],
    'Film': [
      { label: 'Option & License Agreement', value: 'option-license' },
      { label: 'Talent Agreement', value: 'talent-agreement' },
      { label: 'Distribution Agreement', value: 'distribution-agreement' },
      { label: 'Sync License Agreement', value: 'sync-license' }
    ],
    'Design': [
      { label: 'Work-for-Hire Agreement', value: 'work-for-hire' },
      { label: 'Creative Services Agreement', value: 'creative-services' },
      { label: 'Brand Identity Agreement', value: 'brand-identity' },
      { label: 'Merchandise Licensing Agreement', value: 'merchandise-licensing' }
    ],
    'General Business': [
      { label: 'Independent Contractor Agreement', value: 'independent-contractor' },
      { label: 'Joint Venture Agreement', value: 'joint-venture' },
      { label: 'NIL/Endorsement Agreement', value: 'nil-endorsement' },
      { label: 'Revenue Share Agreement', value: 'revenue-share' }
    ]
  }
  
  const tabs = ['Music', 'Film', 'Design', 'General Business']
  const [activeTab, setActiveTab] = useState(tabs[0])
  
  const gradientsByTab = {
    'Music': 'from-[#a29700] to-[#a95a00]', // Orange to Golden (like vinyl records/warm stage lights)
    'Film': 'from-[#5d7597] to-[#2a364a]',    // Dark gray (film reel/cinema)
    'Design': 'from-[#48ec8c] to-[#005f20]',   // Bright green (creative/artistic)
    'General Business': 'from-[#6E53B8] to-[#3a618f]'   // Original gradient for general business
  }
  
  const currentItems = contractsByTab[activeTab as keyof typeof contractsByTab]
  const currentGradient = gradientsByTab[activeTab as keyof typeof gradientsByTab]
  
  return (
    <div className="py-8 md:py-32">
      <div className="h-4 md:h-32"></div>
      <div>
        {/* tabs */}
        <div className="flex gap-4 mb-8 relative">
          {tabs.map((tab) => (
            <motion.button key={tab} onClick={() => setActiveTab(tab)} className={cn("relative text-lg py-1", {
              'text-white': tab === activeTab,
              'text-neutral-600': tab !== activeTab,
            })}>
              {tab}
            { tab === activeTab && <motion.div className="h-[1px] w-full bg-white absolute -bottom-0 left-0" layoutId="tab-indicator" /> }
            </motion.button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div 
            key={activeTab}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1}}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
          >
            {currentItems.map((item, index) => (
              <ContractCardItem
                key={item.value}
                onClick={() => setSelectedScreen('fill')}
                title={item.label}
                color={item.value}
                gradient={currentGradient}
                icon={<ContractTypeIcon type={item.value as any} />}
              />
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  )
}

type ContractCardProps = {
  title: string;
  color: string;
  gradient: string;
  icon: React.ReactNode;
  onClick: () => void;
} & React.ButtonHTMLAttributes<HTMLButtonElement>

function ContractCardItem({ title, color, gradient, icon, onClick }: ContractCardProps) {
  return (
    <button onClick={onClick} className="p-4 w-full aspect-[10/16] grid place-items-center md:h-[240px] bg-neutral-800 rounded-2xl hover:bg-neutral-700 transition-colors cursor-pointer">
      <div className="flex flex-col items-center gap-6">
        <div className={cn("w-24 h-24 rounded-full text-white/50 bg-gradient-to-b grid place-items-center", gradient)}>
          {icon}
        </div>
        <p className="text-white/70 text-lg font-medium text-center">{title}</p>
      </div>
    </button>
  )
}
